import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../api';
import { theme } from '../../theme';
import {
  SubathonElements,
  SubathonOverlayConfig,
  SubathonOverlayName,
  SubathonOverlaysPayload,
  SubathonPresetInfo,
  SubathonSoundConfig,
  SubathonSoundId,
  SubathonStyle,
} from '../../types/subathon';
import { playSubathonSound } from './subathonSounds';
import {
  Button,
  ErrorText,
  Input,
  Label,
  Muted,
  Select,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
  TabBar,
  TabBtn,
  Toast,
  Toolbar,
} from '../ui';

const OVERLAY_LABELS: Record<SubathonOverlayName, string> = {
  timer: 'Contador',
  summary: 'Resumen',
};

/**
 * A diferencia del editor de cowork, acá los controles no viven en una columna
 * angosta: la vista previa va arriba y los ajustes se reparten en tarjetas por
 * tema, así se encuentra todo de un vistazo.
 */
const Groups = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  gap: 0.75rem;
  align-items: start;
`;

const Group = styled.div`
  border: 1px solid rgba(255, 20, 147, 0.14);
  border-radius: 12px;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.02);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const GroupTitle = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${theme.colors.pinkSoft};
  font-weight: 700;
`;

const Field = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 84px;
  gap: 0.5rem;
  align-items: center;
`;

const Range = styled.input`
  width: 100%;
  accent-color: ${theme.colors.pink};
`;

const ColorInput = styled.input`
  width: 100%;
  height: 30px;
  padding: 2px;
  border-radius: 7px;
  border: 1px solid rgba(255, 20, 147, 0.22);
  background: rgba(10, 7, 22, 0.65);
  cursor: pointer;
`;

const Check = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.82rem;
  color: ${theme.colors.textMuted};
  cursor: pointer;

  input {
    accent-color: ${theme.colors.pink};
    width: 15px;
    height: 15px;
  }
`;

const Canvas = styled.div<{ $bg: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 46vh;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 20, 147, 0.2);
  background: ${({ $bg }) =>
    $bg === 'light'
      ? '#e8e8ec'
      : $bg === 'dark'
        ? '#101014'
        : $bg === 'scene'
          ? 'linear-gradient(135deg, #2a1d3d 0%, #123243 55%, #3d2a1d 100%)'
          : 'repeating-conic-gradient(#2a2a2f 0% 25%, #1c1c20 0% 50%) 50% / 22px 22px'};

  iframe {
    position: absolute;
    inset: 0;
    width: 1920px;
    height: 1080px;
    transform-origin: top left;
    border: 0;
    background: transparent;
  }
`;

const UrlBox = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;

  input {
    font-family: 'Consolas', monospace;
    font-size: 0.78rem;
    min-width: 260px;
    flex: 1;
  }
`;

const Hint = styled.small`
  color: ${theme.colors.textMuted};
  font-size: 0.74rem;
  line-height: 1.35;
`;

/* ------------------------------------------------------------------ */

function resolvePresetStyle(presets: SubathonPresetInfo[], presetId: string): Partial<SubathonStyle> {
  const preset = presets.find((p) => p.id === presetId);
  if (!preset) return {};
  if (preset.builtIn) return preset.style;

  const base = presets.find((p) => p.id === (preset.basePreset || 'eugenial'));
  return { ...(base?.style || {}), ...preset.style };
}

function diffStyle(draft: SubathonStyle, base: Partial<SubathonStyle>): Partial<SubathonStyle> {
  const out: Partial<SubathonStyle> = {};
  Object.entries(draft).forEach(([key, value]) => {
    if (JSON.stringify(value) !== JSON.stringify((base as any)[key])) {
      (out as any)[key] = value;
    }
  });
  return out;
}

interface ElementControl {
  key: string;
  label: string;
  type: 'bool' | 'number' | 'text' | 'select';
  options?: { value: string; label: string }[];
  hint?: string;
}

const ELEMENT_CONTROLS: Record<SubathonOverlayName, ElementControl[]> = {
  timer: [
    { key: 'width', label: 'Ancho del panel (px)', type: 'number', hint: '0 = se adapta al contenido.' },
    { key: 'showTitle', label: 'Mostrar título', type: 'bool' },
    { key: 'title', label: 'Título', type: 'text' },
    { key: 'showHours', label: 'Mostrar horas', type: 'bool', hint: 'Sin horas muestra solo minutos y segundos.' },
    { key: 'showSeconds', label: 'Mostrar segundos', type: 'bool' },
    { key: 'showStatePill', label: 'Mostrar estado (pausa / esperando)', type: 'bool' },
    { key: 'showTotalAdded', label: 'Mostrar cuánto se sumó', type: 'bool' },
    { key: 'showCap', label: 'Mostrar el tope', type: 'bool' },
    { key: 'showCapBar', label: 'Barra de avance hacia el tope', type: 'bool' },
    { key: 'showEndText', label: 'Mostrar cartel al terminar', type: 'bool' },
    { key: 'endText', label: 'Texto del final', type: 'text' },
  ],
  summary: [
    { key: 'width', label: 'Ancho del panel (px)', type: 'number', hint: '0 = se adapta al contenido.' },
    { key: 'showTitle', label: 'Mostrar título', type: 'bool' },
    { key: 'title', label: 'Título', type: 'text' },
    {
      key: 'showDuration',
      label: 'Mostrar la fila de números',
      type: 'bool',
      hint: 'La fila de arriba con En vivo, Extendido y Aportes.',
    },
    { key: 'showElapsed', label: 'Dentro de la fila: En vivo', type: 'bool' },
    { key: 'showAdded', label: 'Dentro de la fila: Extendido', type: 'bool' },
    { key: 'showEventsCount', label: 'Dentro de la fila: Aportes', type: 'bool' },
    { key: 'showBreakdown', label: 'Mostrar desglose por tipo', type: 'bool' },
    { key: 'showTopContributors', label: 'Mostrar quiénes aportaron más', type: 'bool' },
    { key: 'topCount', label: 'Cuántas personas listar', type: 'number' },
    { key: 'compact', label: 'Formato compacto', type: 'bool' },
  ],
};

const FEED_CONTROLS: ElementControl[] = [
  {
    key: 'showFeed',
    label: 'Mostrar avisos de aportes',
    type: 'bool',
    hint: 'El renglón tipo «+2 min · sub de Juan». Es visual, no suena.',
  },
  { key: 'feedItems', label: 'Cuántos avisos a la vez', type: 'number' },
  { key: 'feedSeconds', label: 'Segundos que dura cada aviso', type: 'number' },
  {
    key: 'feedPosition',
    label: 'Dónde van los avisos',
    type: 'select',
    options: [
      { value: 'below', label: 'Abajo del reloj' },
      { value: 'above', label: 'Arriba del reloj' },
    ],
  },
];

const SHAPE_OPTIONS = [
  { value: 'rounded', label: 'Redondeada' },
  { value: 'pill', label: 'Cápsula' },
  { value: 'square', label: 'Recta' },
  { value: 'cut', label: 'Esquinas biseladas' },
];

const SHADOW_OPTIONS = [
  { value: 'soft', label: 'Difusa' },
  { value: 'hard', label: 'Dura (desplazada)' },
  { value: 'none', label: 'Sin sombra' },
];

const DEFAULT_SOUND: SubathonSoundConfig = {
  enabled: false,
  volume: 55,
  end: 'chime',
  warning: 'none',
  warningMinutes: 5,
};

const SubathonOverlaysTab: React.FC = () => {
  const [payload, setPayload] = useState<SubathonOverlaysPayload | null>(null);
  const [overlay, setOverlay] = useState<SubathonOverlayName>('timer');
  const [draftStyle, setDraftStyle] = useState<SubathonStyle | null>(null);
  const [draftElements, setDraftElements] = useState<SubathonElements>({});
  const [preset, setPreset] = useState('eugenial');
  const [background, setBackground] = useState('scene');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [canvasWidth, setCanvasWidth] = useState(0);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async (target: SubathonOverlayName) => {
    try {
      const { data } = await api.get<SubathonOverlaysPayload>('/subathon/overlays');
      setPayload(data);
      const config = data.configs.find((c) => c.overlay === target);
      if (config) {
        setPreset(config.preset);
        setDraftStyle(config.style);
        setDraftElements(config.elements);
      }
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudieron cargar los overlays');
    }
  }, []);

  useEffect(() => {
    load(overlay);
  }, [load, overlay]);

  useEffect(() => {
    const measure = () => setCanvasWidth(canvasRef.current?.clientWidth || 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [payload]);

  // Vista previa en vivo: el overlay real recibe el borrador sin guardar nada.
  useEffect(() => {
    if (!draftStyle) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'subathon:preview', overlay, style: draftStyle, elements: draftElements },
      '*',
    );
  }, [draftStyle, draftElements, overlay]);

  useEffect(() => {
    const onReady = (event: MessageEvent) => {
      if (event.data?.type !== 'subathon:preview-ready' || !draftStyle) return;
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'subathon:preview', overlay, style: draftStyle, elements: draftElements },
        '*',
      );
    };
    window.addEventListener('message', onReady);
    return () => window.removeEventListener('message', onReady);
  }, [draftStyle, draftElements, overlay]);

  const config: SubathonOverlayConfig | undefined = useMemo(
    () => payload?.configs.find((c) => c.overlay === overlay),
    [payload, overlay],
  );

  const basePresetStyle = useMemo(
    () => (payload ? resolvePresetStyle(payload.presets, preset) : {}),
    [payload, preset],
  );

  if (error && !payload) return <ErrorText>{error}</ErrorText>;
  if (!payload || !draftStyle || !config) return <Muted>Cargando overlays…</Muted>;

  const setStyle = (changes: Partial<SubathonStyle>) => setDraftStyle({ ...draftStyle, ...changes });
  const setElement = (key: string, value: any) => setDraftElements({ ...draftElements, [key]: value });

  const sound: SubathonSoundConfig = { ...DEFAULT_SOUND, ...(draftStyle.sound || {}) };
  const setSound = (changes: Partial<SubathonSoundConfig>) => setStyle({ sound: { ...sound, ...changes } });

  const applyPreset = (presetId: string) => {
    setPreset(presetId);
    const style = resolvePresetStyle(payload.presets, presetId);
    setDraftStyle({ ...draftStyle, ...style } as SubathonStyle);
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.put(`/subathon/overlays/${overlay}`, {
        preset,
        style: diffStyle(draftStyle, basePresetStyle),
        replaceStyle: true,
        elements: draftElements,
      });
      await load(overlay);
      setMessage('Overlay guardado. OBS se actualiza solo.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const restore = async () => {
    await api.put(`/subathon/overlays/${overlay}`, {
      preset,
      resetStyle: true,
      elements: payload.defaults[overlay],
    });
    await load(overlay);
    setMessage('Valores del preset restaurados.');
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) return;
    await api.post('/subathon/presets', {
      name,
      basePreset: payload.presets.find((p) => p.id === preset)?.builtIn ? preset : 'eugenial',
      style: draftStyle,
    });
    setPresetName('');
    await load(overlay);
    setMessage(`Preset «${name}» guardado.`);
  };

  const deletePreset = async () => {
    const current = payload.presets.find((p) => p.id === preset);
    if (!current || current.builtIn) return;
    if (!window.confirm(`¿Borrar el preset «${current.name}»?`)) return;
    await api.delete(`/subathon/presets/${preset}`);
    setPreset('eugenial');
    await load(overlay);
    setMessage('Preset eliminado.');
  };

  const renamePreset = async () => {
    const current = payload.presets.find((p) => p.id === preset);
    if (!current || current.builtIn) return;
    const name = window.prompt('Nuevo nombre del preset', current.name);
    if (!name?.trim()) return;
    await api.put(`/subathon/presets/${preset}`, { name: name.trim() });
    await load(overlay);
  };

  const url = payload.urls[overlay];
  const hint = payload.hints[overlay];
  const scale = canvasWidth > 0 ? canvasWidth / 1920 : 0;
  const currentPreset = payload.presets.find((p) => p.id === preset);

  const numberField = (label: string, key: keyof SubathonStyle, min: number, max: number, step = 1) => (
    <Field key={String(key)}>
      <div>
        <Label style={{ margin: 0 }}>{label}</Label>
        <Range
          type="range"
          min={min}
          max={max}
          step={step}
          value={Number(draftStyle[key]) || 0}
          onChange={(e) => setStyle({ [key]: Number(e.target.value) } as any)}
        />
      </div>
      <Input
        type="number"
        value={String(draftStyle[key] ?? 0)}
        onChange={(e) => setStyle({ [key]: Number(e.target.value) } as any)}
      />
    </Field>
  );

  const colorField = (label: string, key: keyof SubathonStyle) => (
    <Field key={String(key)}>
      <Label style={{ margin: 0 }}>{label}</Label>
      <ColorInput
        type="color"
        value={/^#[0-9a-f]{6}$/i.test(String(draftStyle[key])) ? String(draftStyle[key]) : '#ffffff'}
        onChange={(e) => setStyle({ [key]: e.target.value } as any)}
      />
    </Field>
  );

  const selectField = (label: string, key: keyof SubathonStyle, options: { value: string; label: string }[]) => (
    <div key={String(key)}>
      <Label>{label}</Label>
      <Select value={String(draftStyle[key])} onChange={(e) => setStyle({ [key]: e.target.value } as any)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );

  const checkField = (label: string, key: keyof SubathonStyle) => (
    <Check key={String(key)}>
      <input
        type="checkbox"
        checked={!!draftStyle[key]}
        onChange={(e) => setStyle({ [key]: e.target.checked } as any)}
      />
      {label}
    </Check>
  );

  const elementField = (control: ElementControl) => {
    const value = draftElements[control.key];

    if (control.type === 'bool') {
      return (
        <div key={control.key}>
          <Check>
            <input
              type="checkbox"
              checked={value !== false}
              onChange={(e) => setElement(control.key, e.target.checked)}
            />
            {control.label}
          </Check>
          {control.hint && <Hint>{control.hint}</Hint>}
        </div>
      );
    }

    if (control.type === 'select') {
      return (
        <div key={control.key}>
          <Label>{control.label}</Label>
          <Select value={String(value ?? '')} onChange={(e) => setElement(control.key, e.target.value)}>
            {(control.options || []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          {control.hint && <Hint>{control.hint}</Hint>}
        </div>
      );
    }

    return (
      <div key={control.key}>
        <Label>{control.label}</Label>
        <Input
          type={control.type === 'number' ? 'number' : 'text'}
          value={value ?? ''}
          onChange={(e) =>
            setElement(control.key, control.type === 'number' ? Number(e.target.value) : e.target.value)
          }
        />
        {control.hint && <Hint>{control.hint}</Hint>}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      {message && <Toast>{message}</Toast>}
      {error && <ErrorText>{error}</ErrorText>}

      <TabBar>
        {(Object.keys(OVERLAY_LABELS) as SubathonOverlayName[]).map((name) => (
          <TabBtn key={name} $active={overlay === name} onClick={() => setOverlay(name)}>
            {OVERLAY_LABELS[name]}
          </TabBtn>
        ))}
      </TabBar>

      <Surface>
        <SurfaceHeader>
          <SurfaceTitle>Vista previa</SurfaceTitle>
          <Toolbar>
            <Select value={background} onChange={(e) => setBackground(e.target.value)}>
              <option value="scene">Fondo de escena</option>
              <option value="dark">Fondo oscuro</option>
              <option value="light">Fondo claro</option>
              <option value="grid">Cuadrícula (transparencia)</option>
            </Select>
            <Button $size="sm" disabled={saving} onClick={save}>
              {saving ? 'Guardando…' : 'Guardar overlay'}
            </Button>
            <Button $size="sm" $variant="ghost" onClick={restore}>
              Restaurar preset
            </Button>
          </Toolbar>
        </SurfaceHeader>
        <SurfaceBody>
          <Canvas ref={canvasRef} $bg={background}>
            {scale > 0 && (
              <iframe
                ref={iframeRef}
                title={`preview-${overlay}`}
                src={`${url}?demo=1`}
                style={{ transform: `scale(${scale})` }}
              />
            )}
          </Canvas>

          <UrlBox style={{ marginTop: '0.7rem' }}>
            <Input readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
            <Button
              $size="sm"
              $variant="ghost"
              onClick={() => {
                navigator.clipboard?.writeText(url);
                setMessage('URL copiada. Pegala en OBS como Browser Source.');
              }}
            >
              Copiar URL
            </Button>
            <Hint>
              En OBS: {hint.width}×{hint.height}, {hint.fps} fps, fondo transparente.
            </Hint>
          </UrlBox>
        </SurfaceBody>
      </Surface>

      <Surface>
        <SurfaceHeader>
          <SurfaceTitle>Ajustes del {OVERLAY_LABELS[overlay].toLowerCase()}</SurfaceTitle>
        </SurfaceHeader>
        <SurfaceBody>
          <Groups>
            <Group>
              <GroupTitle>Preset</GroupTitle>
              <Select value={preset} onChange={(e) => applyPreset(e.target.value)}>
                {payload.presets.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                    {item.builtIn ? '' : ' (propio)'}
                  </option>
                ))}
              </Select>
              {currentPreset?.description && <Hint>{currentPreset.description}</Hint>}
              <Label style={{ marginTop: '0.3rem' }}>Guardar lo de ahora como preset nuevo</Label>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <Input
                  placeholder="Nombre"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                />
                <Button $size="sm" disabled={!presetName.trim()} onClick={savePreset}>
                  Guardar
                </Button>
              </div>
              {currentPreset && !currentPreset.builtIn && (
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <Button $size="sm" $variant="ghost" onClick={renamePreset}>
                    Renombrar
                  </Button>
                  <Button $size="sm" $variant="danger" onClick={deletePreset}>
                    Borrar preset
                  </Button>
                </div>
              )}
            </Group>

            <Group>
              <GroupTitle>Colores</GroupTitle>
              {colorField('Texto', 'textColor')}
              {colorField('Principal', 'accentColor')}
              {colorField('Secundario', 'secondaryColor')}
              {colorField('Apagado', 'mutedColor')}
              {colorField('Fondo del panel', 'backgroundColor')}
              {numberField('Opacidad del fondo', 'backgroundOpacity', 0, 100)}
              {colorField('Color de apuro', 'urgentColor')}
              {numberField('Apuro cuando falten (min)', 'urgentUnderMinutes', 0, 60)}
            </Group>

            <Group>
              <GroupTitle>Tipografía</GroupTitle>
              <div>
                <Label>Fuente del reloj</Label>
                <Select
                  value={draftStyle.fontFamily}
                  onChange={(e) => setStyle({ fontFamily: e.target.value })}
                >
                  {payload.fonts.map((font) => (
                    <option key={font.id} value={font.value}>
                      {font.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>Fuente del texto</Label>
                <Select
                  value={draftStyle.bodyFontFamily}
                  onChange={(e) => setStyle({ bodyFontFamily: e.target.value })}
                >
                  {payload.fonts.map((font) => (
                    <option key={font.id} value={font.value}>
                      {font.label}
                    </option>
                  ))}
                </Select>
              </div>
              {numberField('Tamaño del reloj', 'timerFontSize', 24, 220)}
              {numberField('Tamaño del título', 'titleFontSize', 8, 48)}
              {numberField('Tamaño del texto', 'bodyFontSize', 10, 42)}
              {numberField('Grosor', 'fontWeight', 300, 900, 100)}
              {numberField('Espaciado de letras', 'letterSpacing', 0, 12, 0.5)}
              {checkField('Títulos en mayúscula', 'uppercaseLabels')}
              {checkField('Sombra en el texto', 'textShadow')}
              {checkField('Números de ancho fijo', 'monoDigits')}
            </Group>

            <Group>
              <GroupTitle>Panel</GroupTitle>
              {selectField('Forma', 'panelShape', SHAPE_OPTIONS)}
              {numberField('Redondeo', 'borderRadius', 0, 60)}
              {numberField('Grosor del borde', 'borderWidth', 0, 8)}
              {colorField('Color del borde', 'borderColor')}
              {selectField('Sombra', 'shadowStyle', SHADOW_OPTIONS)}
              {numberField('Fuerza de la sombra', 'shadowStrength', 0, 100)}
              {numberField('Glow', 'glowStrength', 0, 100)}
              {numberField('Desenfoque del fondo', 'backdropBlur', 0, 20)}
              {numberField('Relleno', 'padding', 0, 60)}
              {numberField('Separación', 'gap', 0, 40)}
              {numberField('Escala general (%)', 'scale', 50, 200)}
            </Group>

            <Group>
              <GroupTitle>Ubicación del reloj</GroupTitle>
              {selectField('Alineación general', 'align', [
                { value: 'left', label: 'Izquierda' },
                { value: 'center', label: 'Centro' },
                { value: 'right', label: 'Derecha' },
              ])}
              {selectField('Disposición', 'layout', [
                { value: 'stacked', label: 'Apilado (título arriba)' },
                { value: 'inline', label: 'En línea (barra)' },
              ])}
              {selectField('Alineación del reloj', 'timerAlign', [
                { value: 'inherit', label: 'Igual que el resto' },
                { value: 'left', label: 'Izquierda' },
                { value: 'center', label: 'Centro' },
                { value: 'right', label: 'Derecha' },
              ])}
              {numberField('Corrimiento horizontal', 'timerOffsetX', -200, 200)}
              {numberField('Corrimiento vertical', 'timerOffsetY', -200, 200)}
              {checkField('Reloj en caja propia', 'timerBoxed')}
              {checkField('Un recuadro por dígito', 'digitBoxes')}
            </Group>

            <Group>
              <GroupTitle>Detalles</GroupTitle>
              {checkField('Barra de acento', 'accentBar')}
              {selectField('Lado de la barra', 'accentBarPosition', [
                { value: 'left', label: 'Izquierda' },
                { value: 'top', label: 'Arriba' },
              ])}
              {numberField('Grosor de la barra', 'accentBarThickness', 1, 16)}
              {checkField('Línea bajo el título', 'labelDivider')}
              {selectField('Estilo de las barras', 'barStyle', [
                { value: 'glow', label: 'Con glow' },
                { value: 'flat', label: 'Plana' },
              ])}
              {numberField('Grosor de las barras', 'barThickness', 2, 24)}
            </Group>

            <Group>
              <GroupTitle>Animación</GroupTitle>
              {checkField('Animaciones activas', 'animationsEnabled')}
              {numberField('Velocidad (%)', 'animationSpeed', 40, 200)}
              {checkField('Latido al entrar un aporte', 'pulseOnCredit')}
            </Group>

            <Group>
              <GroupTitle>Contenido</GroupTitle>
              {ELEMENT_CONTROLS[overlay].map(elementField)}
            </Group>

            {overlay === 'timer' && (
              <Group>
                <GroupTitle>Avisos de aportes</GroupTitle>
                {FEED_CONTROLS.map(elementField)}
              </Group>
            )}

            {overlay === 'timer' && (
              <Group>
                <GroupTitle>Sonido al llegar a cero</GroupTitle>
                <Check>
                  <input
                    type="checkbox"
                    checked={sound.enabled}
                    onChange={(e) => setSound({ enabled: e.target.checked })}
                  />
                  Reproducir sonido en este overlay
                </Check>
                <Hint>
                  Además tiene que estar permitido el sonido en Reglas. OBS necesita que la fuente no
                  esté silenciada.
                </Hint>
                <div>
                  <Label>Al terminar</Label>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <Select
                      value={sound.end}
                      onChange={(e) => setSound({ end: e.target.value as SubathonSoundId })}
                    >
                      {payload.sounds.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.label}
                        </option>
                      ))}
                    </Select>
                    <Button
                      $size="sm"
                      $variant="ghost"
                      onClick={() => playSubathonSound(sound.end, sound.volume)}
                    >
                      Probar
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Aviso antes del final</Label>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <Select
                      value={sound.warning}
                      onChange={(e) => setSound({ warning: e.target.value as SubathonSoundId })}
                    >
                      {payload.sounds.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.label}
                        </option>
                      ))}
                    </Select>
                    <Button
                      $size="sm"
                      $variant="ghost"
                      onClick={() => playSubathonSound(sound.warning, sound.volume)}
                    >
                      Probar
                    </Button>
                  </div>
                </div>
                <Field>
                  <div>
                    <Label style={{ margin: 0 }}>Cuántos minutos antes</Label>
                    <Range
                      type="range"
                      min={1}
                      max={30}
                      value={sound.warningMinutes}
                      onChange={(e) => setSound({ warningMinutes: Number(e.target.value) })}
                    />
                  </div>
                  <Input
                    type="number"
                    value={sound.warningMinutes}
                    onChange={(e) => setSound({ warningMinutes: Number(e.target.value) })}
                  />
                </Field>
                <Field>
                  <div>
                    <Label style={{ margin: 0 }}>Volumen</Label>
                    <Range
                      type="range"
                      min={0}
                      max={100}
                      value={sound.volume}
                      onChange={(e) => setSound({ volume: Number(e.target.value) })}
                    />
                  </div>
                  <Input
                    type="number"
                    value={sound.volume}
                    onChange={(e) => setSound({ volume: Number(e.target.value) })}
                  />
                </Field>
              </Group>
            )}
          </Groups>
        </SurfaceBody>
      </Surface>
    </div>
  );
};

export default SubathonOverlaysTab;
